import React, { useState } from 'react';
import {
  wireInstructionsReady, missingWireFields, WIRE_NOT_READY,
} from '../constants/wireReadiness';

// Wire instruction env vars (set in Render):
//   WIRE_BANK_NAME, WIRE_ROUTING_NUMBER, WIRE_ACCOUNT_NUMBER,
//   WIRE_ACCOUNT_NAME, WIRE_BANK_ADDRESS
// Read server-side and passed through `wireData` from the option-period
// dashboard endpoint. See backend/routes/option-period.js for the source.

// ⚠️ DEFAULT IS down_payment, NOT emd. Doorly collects the borrower's full
// down payment and charges no earnest money, so an omitted wireType must not
// fall back to labelling that money "Earnest Money Deposit". Every current
// call site passes down_payment or ctc explicitly — the old 'emd' default was
// unreachable today and would have mislabelled the first caller who forgot it.
const WireInstructionsModal = ({ isOpen, onClose, loanNumber, borrowerName, wireType = 'down_payment', amount, wireData, emdBreakdown }) => {
  const [copied, setCopied] = useState(null);

  if (!isOpen) return null;

  // Wire reference defaults to "<LOAN_NUMBER> - <BORROWER_LAST_NAME>". When
  // wireType is the new "down_payment", prefix it with DP- so admin /
  // Frost Bank can match the wire back to the loan file. EMD-* refs are
  // kept for legacy callers; ctc-* are built upstream by closing.js.
  const lastNameLast4 = (borrowerName || '').replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase() || 'BORROWER';
  const isDownPayment = wireType === 'down_payment';
  const refPrefix = isDownPayment ? 'DP' : null;
  const reference = loanNumber && refPrefix
    ? `${refPrefix}-${loanNumber}-${lastNameLast4}`
    : loanNumber
      ? `${loanNumber} - ${borrowerName || 'BORROWER'}`
      : 'Your loan number and last name';

  const title = isDownPayment ? 'Send Your Down Payment' : 'Wire Closing Funds';
  const subtitle = isDownPayment
    ? 'Send your down payment via wire transfer to Doorly\'s secure escrow account'
    : 'Send your closing funds via wire transfer to Doorly\'s secure escrow account';

  const copyText = (label, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // No hardcoded fallbacks — every wire detail must come through the
  // env-driven wireData payload (Doorly's Frost Bank escrow account). The
  // borrower's counterparty is always Doorly; no title company is ever shown.
  const allPopulated = wireInstructionsReady(wireData);

  // ── 🚨 INCOMPLETE MEANS NOTHING IS SHOWN, AND IT RETURNS EARLY ─────────────
  //
  // Jesse: "It must show no partial wire details at all: one clear message that
  // instructions aren't ready and not to send money until they appear."
  //
  // 🚨 THE OLD BEHAVIOUR RENDERED THE WHOLE FORM. Six rows with "Not available
  // yet" in the absent ones — and a REAL Reference / Memo, because that field
  // has a client-side fallback — above a real wire amount, a real "Amount to
  // Wire" breakdown and a real 2:00 PM deadline. Only the Copy buttons were
  // withheld. A borrower reading three genuine bank fields and one amber line
  // has been taught that the instruction is real and one piece is outstanding,
  // which is precisely the opening a wire-fraud impersonator needs.
  //
  // 🔑 AN EARLY RETURN, NOT A CONDITIONAL AROUND EACH BLOCK. Six separate
  // `allPopulated &&` guards is six chances to add a seventh block without one;
  // nothing after this line can render at all. That is the property the test
  // asserts, and it is a property of the control flow rather than of six
  // correct edits.
  if (!allPopulated) {
    // ⚠️ STAFF-FACING ONLY, AND NAMED. Which field is missing never reaches the
    // screen — it would be a partial detail about a partial instruction — but a
    // console line is what turns "a borrower says it is blank" into an answer.
    if (typeof console !== 'undefined') {
      console.warn('[WireInstructions] withheld — missing:', missingWireFields(wireData).join(', ') || 'all');
    }
    return (
      <div className="safe-modal-overlay" style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.70)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px',
        overflowY: 'auto',
      }}>
        <div className="safe-modal-box" style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '460px', overflowY: 'auto', '--sm-max': 'calc(100vh - 80px)', margin: 'auto 0', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
          <div style={{ background: '#111827', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#acf44e', marginBottom: '4px' }}>Doorly</div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff' }}>{WIRE_NOT_READY.heading}</div>
            </div>
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>
              ✕
            </button>
          </div>
          <div style={{ padding: '24px 28px' }}>
            <div style={{ background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '12px', padding: '16px 18px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: '800', color: '#111827', lineHeight: '1.5' }}>
                {WIRE_NOT_READY.body}
              </p>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#374151', lineHeight: '1.6' }}>
                {WIRE_NOT_READY.fraud}
              </p>
            </div>
          </div>
          <div style={{ background: '#f9fafb', borderTop: '1px solid #f3f4f6', padding: '14px 28px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af' }}>
              Go Doorly, LLC | NMLS #2764749
            </p>
          </div>
        </div>
      </div>
    );
  }

  const fields = [
    { label: 'Bank Name',        value: wireData.bank_name },
    { label: 'Bank Address',     value: wireData.bank_address },
    { label: 'Account Name',     value: wireData.account_name },
    { label: 'Account Number',   value: wireData.account, sensitive: true },
    { label: 'Routing Number',   value: wireData.routing, sensitive: true },
    { label: 'Reference / Memo', value: wireData.reference || reference },
  ];

  return (
    <div className="safe-modal-overlay" style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.70)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px',
      overflowY: 'auto',
    }}>
      <div className="safe-modal-box" style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '460px', overflowY: 'auto', '--sm-max': 'calc(100vh - 80px)', margin: 'auto 0', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ background: '#111827', padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#acf44e', marginBottom: '4px' }}>Doorly</div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff' }}>{title}</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{subtitle}</div>
            </div>
            <button onClick={onClose}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>
              ✕
            </button>
          </div>
          {amount && (
            <div style={{ marginTop: '14px', background: 'rgba(172,244,78,0.15)', border: '1px solid rgba(172,244,78,0.3)', borderRadius: '10px', padding: '10px 14px', display: 'inline-block' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Wire Amount</span>
              <div style={{ fontSize: '22px', fontWeight: '900', color: '#acf44e' }}>${Number(amount).toLocaleString()}</div>
            </div>
          )}
        </div>

        {/* Warning */}
        <div style={{ background: '#fff0f3', borderBottom: '1px solid #ffc5ca', padding: '12px 28px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: '12px', color: '#374151', lineHeight: '1.6', fontWeight: '600' }}>
            Wire fraud is common. Always verify wire instructions by calling Doorly directly before sending funds (support@godoorly.com | 512-713-6016). We will never ask you to change wire instructions via email or text.
          </p>
        </div>

        {/* ⚠️ GATED ON isDownPayment, NOT isEMD. This block was gated on a
            wireType no call site ever passes, so it never rendered — while
            Dashboard's down-payment modal passes emdBreakdown expecting exactly
            this. The breakdown a borrower needs to understand what they are
            wiring was present in the props and invisible on screen. */}
        {isDownPayment && emdBreakdown?.emdRemaining > 0 && (
          <div style={{ background: '#3a6ad9', color: '#fff', padding: '18px 28px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              Amount to Wire
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, color: '#fff', letterSpacing: -0.5, marginBottom: 8 }}>
              ${Number(emdBreakdown.emdRemaining).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
              This is your full down payment. The Purchase Initiation Deposit you've already paid is credited in full toward your purchase on your Closing Disclosure.
            </div>
          </div>
        )}

        {/* Fields */}
        <div style={{ padding: '20px 28px' }}>
          {/* 🔑 NO "not yet available" NOTICE HERE ANY MORE. This branch is only
              reached when every required field is present, so a notice saying
              otherwise could only ever be wrong. The not-ready case returns
              above and renders nothing but the message. */}
          {fields.map(field => (
            <div key={field.label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px',
              background: field.sensitive ? '#f9fafb' : '#fff',
              border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '8px',
            }}>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                  {field.label}
                </div>
                <div style={{ fontSize: '15px', fontWeight: '800', color: '#111827', fontFamily: field.sensitive ? 'monospace' : 'inherit' }}>
                  {field.value}
                </div>
              </div>
              {/* 🔑 UNCONDITIONAL. Copy was gated on allPopulated when this
                  branch could still be reached with missing fields; it cannot
                  now, and a dead guard reads as though a partial render is
                  still possible. */}
              <button
                onClick={() => copyText(field.label, field.value)}
                style={{
                  padding: '5px 10px',
                  background: copied === field.label ? '#f4fee8' : '#f3f4f6',
                  border: `1px solid ${copied === field.label ? '#d4f99a' : '#e5e7eb'}`,
                  borderRadius: '6px', cursor: 'pointer',
                  fontSize: '11px', fontWeight: '700',
                  color: copied === field.label ? '#2d6a0a' : '#374151',
                  flexShrink: 0, marginLeft: '12px',
                }}>
                {copied === field.label ? 'Copied' : 'Copy'}
              </button>
            </div>
          ))}

          <div style={{ marginTop: '12px', padding: '12px 14px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: '10px', fontSize: '12px', color: '#7a5200', lineHeight: '1.6' }}>
            <strong>Important:</strong> Wire must be received by 2:00 PM local time on the deadline date. Contact Doorly if you have questions.
          </div>
        </div>

        {/* Footer */}
        <div style={{ background: '#f9fafb', borderTop: '1px solid #f3f4f6', padding: '14px 28px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6b7280' }}>
            Questions about wiring? Call 512-713-6016.
          </p>
          <p style={{ margin: 0, fontSize: '11px', color: '#d1d5db' }}>
            Go Doorly, LLC | NMLS #2764749 | These instructions are encrypted in transit.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WireInstructionsModal;
